import { useState } from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { createLogger } from '@/lib/logger'
import { useUserPreferencesStore } from '@/store/user-preferences.store'
import { toast } from 'sonner'

import type {
  TaskDueSoonLeadDays,
  TaskReminderPreferences,
} from '@scrambl/shared'
import {
  PreferenceContextEnum,
  PreferenceScopeEnum,
  resolveTaskDueSoonLeadDays,
  TASK_DUE_SOON_LEAD_DAY_OPTIONS,
} from '@scrambl/shared'

const logger = createLogger(
  'components/notifications/TaskDueSoonLeadTimeSelect',
)

const leadTimeLabel = (days: TaskDueSoonLeadDays) =>
  days === 1 ? '1 day before' : `${days} days before`

/**
 * "Remind me before due date" for the Task Due Soon row. Stored per user and
 * workspace under the TASK_REMINDERS preference; the daily reminder sweep
 * reads it for both the in-app reminder and the email digest.
 */
export const TaskDueSoonLeadTimeSelect = () => {
  const storedPreferences = useUserPreferencesStore(
    state =>
      state.getPreference(
        PreferenceContextEnum.TASK_REMINDERS,
        undefined,
        PreferenceScopeEnum.USER,
      )?.preferences,
  )
  const savePreference = useUserPreferencesStore(state => state.savePreference)
  const [draft, setDraft] = useState<TaskDueSoonLeadDays | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const value = draft ?? resolveTaskDueSoonLeadDays(storedPreferences)

  const handleChange = async (next: string) => {
    const days = resolveTaskDueSoonLeadDays({ dueSoonLeadDays: Number(next) })
    if (days === value) return
    setDraft(days)
    setIsSaving(true)
    try {
      const preferences: TaskReminderPreferences = {
        ...(storedPreferences as TaskReminderPreferences | undefined),
        dueSoonLeadDays: days,
      }
      await savePreference(
        PreferenceContextEnum.TASK_REMINDERS,
        { ...preferences },
        undefined,
        PreferenceScopeEnum.USER,
      )
      toast.success('Reminder timing saved')
    } catch (error) {
      logger.error('Failed to save task reminder timing:', error)
      toast.error('Could not save reminder timing')
    } finally {
      setDraft(null)
      setIsSaving(false)
    }
  }

  return (
    <div className="mt-1.5 flex items-center gap-2 text-xs text-muted-foreground">
      <span aria-hidden="true">Remind me</span>
      <Select
        value={String(value)}
        onValueChange={handleChange}
        disabled={isSaving}
      >
        <SelectTrigger
          aria-label="Remind me before due date"
          className="h-7 w-[132px] text-xs"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {TASK_DUE_SOON_LEAD_DAY_OPTIONS.map(days => (
            <SelectItem key={days} value={String(days)} className="text-xs">
              {leadTimeLabel(days)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
